import React from "react";
import {Controller, useFormContext} from "react-hook-form";
import * as documentApi from "../../api/documentApi/documentApi";

import HowToRegIcon from "@mui/icons-material/HowToReg";
import MemoryIcon from "@mui/icons-material/Memory";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextareaAutosize,
  Typography,
  styled,
  Divider,
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Slide from "@mui/material/Slide";
import {TransitionProps} from "@mui/material/transitions";
import {useRecoilValue} from "recoil";
import {tokenSelector, userCompanyData} from "../../Recoil/Atom/authState";
import MWCaption from "../../components/MWCaption";
import MWInputDate from "../../components/MWInputDate";
import MWInputFile from "../../components/MWInputFile";
import useMediaQuery from "../../hooks/useMediaQueryHook";
import {useGetQuery} from "../../hooks/useQueryHooks";
import {ResponseList} from "../../models/clientModels/interfaces/Response";
import {
  EQChangeRequest,
  changeStatusNames,
  changeTypeNames,
} from "../../models/serverModels/workOrder/EQChangeRequest";
import {
  EQDocument,
  initializeDocumentListParams,
} from "../../models/serverModels/workOrder/EQDocument";
import {StyledDetailContainer, StyledDetailTable} from "../../styledComponents/StyledDetail";

const WriteTip = styled(Grid)(({theme}) => ({
  width: "100%",
  marginTop: "32px",
  padding: "16px",
  border: `1px solid ${theme.palette.background.paper}`,
  borderRadius: "16px",
  backgroundColor: theme.palette.background.paper,
  boxShadow:
    "rgb(0 0 0 / 10%) 0rem 0.25rem 0.375rem -0.0625rem, rgb(0 0 0 / 6%) 0rem 0.125rem 0.25rem -0.0625rem",
}));

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

type SubTitleColorProps = {
  captionColor?: string;
};

const ChangeRequestForm = ({captionColor}: SubTitleColorProps) => {
  const tokenState = useRecoilValue(tokenSelector);
  const companyData = useRecoilValue(userCompanyData);

  const [open, setOpen] = React.useState(false);

  const isMobile = useMediaQuery("(max-width: 960px)");

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const {data: documentList, isLoading} = useGetQuery<ResponseList<EQDocument>>({
    key: ["DocumentList", {...initializeDocumentListParams, token: tokenState, id: 5}],
    api: () => documentApi.getList({...initializeDocumentListParams, token: tokenState, id: 5}),
  });

  const methods = useFormContext<EQChangeRequest>();

  const {
    setValue,
    getValues,
    watch,
    control,
    formState: {errors},
  } = methods;

  return (
    <React.Fragment>
      <StyledDetailContainer>
        <MWCaption title={"변경요청서 작성 요령"} color={captionColor} icon={<MemoryIcon />} />
        <StyledDetailTable>
          <Grid container justifyContent={"space-evenly"}>
            <WriteTip item xl={5}>
              <Typography fontWeight={500} sx={{whiteSpace: "pre-line"}}>
                {companyData.changeRequestTip1}
              </Typography>
            </WriteTip>
            <WriteTip item xl={5}>
              <Typography fontWeight={500} sx={{whiteSpace: "pre-line"}}>
                {companyData.changeRequestTip2}
              </Typography>
            </WriteTip>
          </Grid>
        </StyledDetailTable>
      </StyledDetailContainer>
      {isMobile ? (
        <StyledDetailContainer>
          <MWCaption title={"변경요청서 작성"} color={captionColor} icon={<HowToRegIcon />} />
          <StyledDetailTable>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell component="th" width="10%">
                    일련번호
                  </TableCell>
                  <TableCell component="td" width="23.33%">
                    {getValues("serialString")}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" width="10%">
                    발의일자
                  </TableCell>
                  <TableCell component="td" width="23.33%">
                    <MWInputDate name="requestDate" />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" width="10%">
                    변경구분
                  </TableCell>
                  <TableCell component="td" width="23.33%">
                    <Controller
                      name="type"
                      control={control}
                      render={({field}) => (
                        <Grid container alignItems="center" justifyContent="flex-start">
                          <Grid item xs={12}>
                            <Select
                              {...field}
                              id="type"
                              size="small"
                              defaultValue={field.value}
                              style={{width: "100%"}}
                            >
                              <MenuItem disabled value={field.value} style={{display: "none"}}>
                                선택하세요
                              </MenuItem>
                              {changeTypeNames !== undefined &&
                                changeTypeNames.map((item, index) => (
                                  <MenuItem key={index} value={index}>
                                    {item}
                                  </MenuItem>
                                ))}
                            </Select>
                          </Grid>
                        </Grid>
                      )}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th">복구예정일</TableCell>
                  <TableCell component="td">
                    <MWInputDate name="estimateDate" />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th">상태</TableCell>
                  <TableCell component="td">
                    <Controller
                      name="status"
                      control={control}
                      render={({field}) => (
                        <Grid container alignItems="center" justifyContent="flex-start">
                          <Grid item xs={12}>
                            <Select
                              {...field}
                              id="status"
                              size="small"
                              defaultValue={field.value}
                              style={{width: "100%"}}
                            >
                              <MenuItem disabled value={field.value} style={{display: "none"}}>
                                선택하세요
                              </MenuItem>
                              {changeStatusNames !== undefined &&
                                changeStatusNames.map((item, index) => (
                                  <MenuItem key={index} value={index}>
                                    {item}
                                  </MenuItem>
                                ))}
                            </Select>
                          </Grid>
                        </Grid>
                      )}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th">반려/지연 사유</TableCell>
                  <TableCell component="td">
                    <Controller
                      name="statusContent"
                      control={control}
                      render={({field: {onChange, value}}) => (
                        <TextareaAutosize
                          defaultValue={value}
                          aria-label="minimum height"
                          minRows={3}
                          onChange={onChange}
                          style={{width: "100%", padding: "8px"}}
                        />
                      )}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th">발의자</TableCell>
                  <TableCell component="td">{getValues("requester.name")}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th">발의부서</TableCell>
                  <TableCell component="td">{getValues("department.name")}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th">변경내용</TableCell>
                  <TableCell component="td">
                    <Controller
                      name="content"
                      control={control}
                      render={({field: {onChange, value}}) => (
                        <TextareaAutosize
                          defaultValue={value}
                          aria-label="minimum height"
                          minRows={3}
                          onChange={onChange}
                          style={{width: "100%", padding: "8px"}}
                        />
                      )}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th">변경등급</TableCell>
                  <TableCell component="td">{getValues("level")}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th">변경검토근거자료</TableCell>
                  <TableCell component="td">
                    <MWInputFile file="base64Attachment" fileName="attachmentFileName" />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th">변경요청서 서식</TableCell>
                  <TableCell component="td">
                    <>
                      <Button
                        variant="contained"
                        size="small"
                        color="success"
                        onClick={handleClickOpen}
                      >
                        서식리스트 열기
                      </Button>
                      <Dialog
                        open={open}
                        TransitionComponent={Transition}
                        keepMounted
                        onClose={handleClose}
                        aria-describedby="alert-dialog-slide-description"
                      >
                        <DialogTitle align="center" fontWeight={600}>
                          {"변경요청서 서식 리스트"}
                        </DialogTitle>
                        <DialogContent>
                          {documentList?.items.map((item, index) => (
                            <Box key={item.uid}>
                              <ListItem disablePadding>
                                <ListItemButton
                                  component="a"
                                  href={`/API/DownloadDocument/${item.uid}?token=${tokenState}`}
                                >
                                  <ListItemText
                                    primary={item.fileName}
                                    primaryTypographyProps={{
                                      fontSize: 12,
                                      color: "#3f51b5",
                                    }}
                                  />
                                </ListItemButton>
                              </ListItem>
                              <Divider />
                            </Box>
                          ))}
                        </DialogContent>
                        <DialogActions>
                          <Button onClick={handleClose}>닫기</Button>
                        </DialogActions>
                      </Dialog>
                    </>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th">완료일</TableCell>
                  <TableCell component="td">
                    <MWInputDate name="completeDate" />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th">변경요구서</TableCell>
                  <TableCell component="td">
                    <MWInputFile file="base64Request" fileName="requestFile" />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th">
                    공정안전자료
                    <br />
                    <Controller
                      name="hasProcessSafety"
                      control={control}
                      render={({field}) => (
                        <FormControlLabel
                          sx={{".MuiFormControlLabel-label": {fontSize: 14, color: "#081f3c"}}}
                          control={
                            <Checkbox
                              id="hasProcessSafety"
                              onChange={(e) => {
                                field.onChange(e.target.checked);
                                setValue("base64ProcessSafety", "");
                                setValue("processSafetyFile", "");
                              }}
                              checked={field.value}
                              sx={{"& .MuiSvgIcon-root": {fontSize: 20}}}
                            />
                          }
                          label="해당없음"
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell component="td">
                    {watch("hasProcessSafety") ? (
                      "해당없음"
                    ) : (
                      <MWInputFile file="base64ProcessSafety" fileName="processSafetyFile" />
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th">
                    공정위험성평가 <br />
                    <Controller
                      name="hasProcessEval"
                      control={control}
                      render={({field}) => (
                        <FormControlLabel
                          sx={{".MuiFormControlLabel-label": {fontSize: 14, color: "#081f3c"}}}
                          control={
                            <Checkbox
                              id="hasProcessEval"
                              onChange={(e) => {
                                field.onChange(e.target.checked);
                                setValue("base64Process", "");
                                setValue("processFile", "");
                              }}
                              checked={field.value}
                              sx={{"& .MuiSvgIcon-root": {fontSize: 20}}}
                            />
                          }
                          label="해당없음"
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell component="td">
                    {watch("hasProcessEval") ? (
                      "해당없음"
                    ) : (
                      <MWInputFile file="base64Process" fileName="processFile" />
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th">
                    안전운전절차 <br />
                    <Controller
                      name="hasSafetyProcedure"
                      control={control}
                      render={({field}) => (
                        <FormControlLabel
                          sx={{".MuiFormControlLabel-label": {fontSize: 14, color: "#081f3c"}}}
                          control={
                            <Checkbox
                              id="hasSafetyProcedure"
                              onChange={(e) => {
                                field.onChange(e.target.checked);
                                setValue("base64Procedure", "");
                                setValue("procedureFile", "");
                              }}
                              checked={field.value}
                              sx={{"& .MuiSvgIcon-root": {fontSize: 20}}}
                            />
                          }
                          label="해당없음"
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell component="td">
                    {watch("hasSafetyProcedure") ? (
                      "해당없음"
                    ) : (
                      <MWInputFile file="base64Procedure" fileName="procedureFile" />
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th">
                    비상조치계획 <br />
                    <Controller
                      name="hasEmergency"
                      control={control}
                      render={({field}) => (
                        <FormControlLabel
                          sx={{".MuiFormControlLabel-label": {fontSize: 14, color: "#081f3c"}}}
                          control={
                            <Checkbox
                              id="hasEmergency"
                              onChange={(e) => {
                                field.onChange(e.target.checked);
                                setValue("base64Emergency", "");
                                setValue("emergencyFile", "");
                              }}
                              checked={field.value}
                              sx={{"& .MuiSvgIcon-root": {fontSize: 20}}}
                            />
                          }
                          label="해당없음"
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell component="td">
                    {watch("hasEmergency") ? (
                      "해당없음"
                    ) : (
                      <MWInputFile file="base64Emergency" fileName="emergencyFile" />
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th">
                    근로자교육평가 <br />
                    <Controller
                      name="hasEducation"
                      control={control}
                      render={({field}) => (
                        <FormControlLabel
                          sx={{".MuiFormControlLabel-label": {fontSize: 14, color: "#081f3c"}}}
                          control={
                            <Checkbox
                              id="hasEducation"
                              onChange={(e) => {
                                field.onChange(e.target.checked);
                                setValue("base64Education", "");
                                setValue("educationFile", "");
                              }}
                              checked={field.value}
                              sx={{"& .MuiSvgIcon-root": {fontSize: 20}}}
                            />
                          }
                          label="해당없음"
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell component="td">
                    {watch("hasEducation") ? (
                      "해당없음"
                    ) : (
                      <MWInputFile file="base64Education" fileName="educationFile" />
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th">가동전점검</TableCell>
                  <TableCell component="td">
                    <MWInputFile file="base64PreCheck" fileName="preCheckFile" />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th"></TableCell>
                  <TableCell component="td"></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </StyledDetailTable>
        </StyledDetailContainer>
      ) : (
        <StyledDetailContainer>
          <MWCaption title={"변경요청서 작성"} color={captionColor} icon={<HowToRegIcon />} />
          <StyledDetailTable>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell component="th" width="10%">
                    일련번호
                  </TableCell>
                  <TableCell component="td" width="23.33%">
                    {getValues("serialString")}
                  </TableCell>
                  <TableCell component="th" width="10%">
                    발의일자
                  </TableCell>
                  <TableCell component="td" width="23.33%">
                    <MWInputDate name="requestDate" />
                  </TableCell>
                  <TableCell component="th" width="10%">
                    변경구분
                  </TableCell>
                  <TableCell component="td" width="23.33%">
                    <Controller
                      name="type"
                      control={control}
                      render={({field}) => (
                        <Grid container alignItems="center" justifyContent="flex-start">
                          <Grid item xs={12}>
                            <Select
                              {...field}
                              id="type"
                              size="small"
                              defaultValue={field.value}
                              style={{width: "100%"}}
                            >
                              <MenuItem disabled value={field.value} style={{display: "none"}}>
                                선택하세요
                              </MenuItem>
                              {changeTypeNames !== undefined &&
                                changeTypeNames.map((item, index) => (
                                  <MenuItem key={index} value={index}>
                                    {item}
                                  </MenuItem>
                                ))}
                            </Select>
                          </Grid>
                        </Grid>
                      )}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th">복구예정일</TableCell>
                  <TableCell component="td">
                    <MWInputDate name="estimateDate" />
                  </TableCell>
                  <TableCell component="th">상태</TableCell>
                  <TableCell component="td">
                    <Controller
                      name="status"
                      control={control}
                      render={({field}) => (
                        <Grid container alignItems="center" justifyContent="flex-start">
                          <Grid item xs={12}>
                            <Select
                              {...field}
                              id="status"
                              size="small"
                              defaultValue={field.value}
                              style={{width: "100%"}}
                            >
                              <MenuItem disabled value={field.value} style={{display: "none"}}>
                                선택하세요
                              </MenuItem>
                              {changeStatusNames !== undefined &&
                                changeStatusNames.map((item, index) => (
                                  <MenuItem key={index} value={index}>
                                    {item}
                                  </MenuItem>
                                ))}
                            </Select>
                          </Grid>
                        </Grid>
                      )}
                    />
                  </TableCell>
                  <TableCell component="th">반려/지연 사유</TableCell>
                  <TableCell component="td">
                    <Controller
                      name="statusContent"
                      control={control}
                      render={({field: {onChange, value}}) => (
                        <TextareaAutosize
                          defaultValue={value}
                          aria-label="minimum height"
                          minRows={3}
                          onChange={onChange}
                          style={{width: "100%", padding: "8px"}}
                        />
                      )}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th">발의자</TableCell>
                  <TableCell component="td">{getValues("requester.name")}</TableCell>
                  <TableCell component="th">발의부서</TableCell>
                  <TableCell component="td">{getValues("department.name")}</TableCell>
                  <TableCell component="th">변경내용</TableCell>
                  <TableCell component="td">
                    <Controller
                      name="content"
                      control={control}
                      render={({field: {onChange, value}}) => (
                        <TextareaAutosize
                          defaultValue={value}
                          aria-label="minimum height"
                          minRows={3}
                          onChange={onChange}
                          style={{width: "100%", padding: "8px"}}
                        />
                      )}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th">변경등급</TableCell>
                  <TableCell component="td">{getValues("level")}</TableCell>
                  <TableCell component="th">변경검토근거자료</TableCell>
                  <TableCell component="td">
                    <MWInputFile file="base64Attachment" fileName="attachmentFileName" />
                  </TableCell>
                  <TableCell component="th">변경요청서 서식</TableCell>
                  <TableCell component="td">
                    <>
                      <Button
                        variant="contained"
                        size="small"
                        color="success"
                        onClick={handleClickOpen}
                      >
                        서식리스트 열기
                      </Button>
                      <Dialog
                        open={open}
                        TransitionComponent={Transition}
                        keepMounted
                        onClose={handleClose}
                        aria-describedby="alert-dialog-slide-description"
                      >
                        <DialogTitle align="center" fontWeight={600}>
                          {"변경요청서 서식 리스트"}
                        </DialogTitle>
                        <DialogContent>
                          {documentList?.items.map((item) => (
                            <Box key={item.uid}>
                              <ListItem disablePadding>
                                <ListItemButton
                                  component="a"
                                  href={`/API/DownloadDocument/${item.uid}?token=${tokenState}`}
                                >
                                  <ListItemText
                                    primary={item.fileName}
                                    primaryTypographyProps={{
                                      fontSize: 14,
                                      color: "#3f51b5",
                                    }}
                                  />
                                </ListItemButton>
                              </ListItem>
                              <Divider />
                            </Box>
                          ))}
                        </DialogContent>
                        <DialogActions>
                          <Button onClick={handleClose}>닫기</Button>
                        </DialogActions>
                      </Dialog>
                    </>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th">완료일</TableCell>
                  <TableCell component="td">
                    <MWInputDate name="completeDate" />
                  </TableCell>
                  <TableCell component="th">변경요구서</TableCell>
                  <TableCell component="td">
                    <MWInputFile file="base64Request" fileName="requestFile" />
                  </TableCell>
                  <TableCell component="th">
                    공정안전자료
                    <br />
                    <Controller
                      name="hasProcessSafety"
                      control={control}
                      render={({field}) => (
                        <FormControlLabel
                          sx={{".MuiFormControlLabel-label": {fontSize: 14, color: "#081f3c"}}}
                          control={
                            <Checkbox
                              id="hasProcessSafety"
                              onChange={(e) => {
                                field.onChange(e.target.checked);
                                setValue("base64ProcessSafety", "");
                                setValue("processSafetyFile", "");
                              }}
                              checked={field.value}
                              sx={{"& .MuiSvgIcon-root": {fontSize: 20}}}
                            />
                          }
                          label="해당없음"
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell component="td">
                    {watch("hasProcessSafety") ? (
                      "해당없음"
                    ) : (
                      <MWInputFile file="base64ProcessSafety" fileName="processSafetyFile" />
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th">
                    공정위험성평가 <br />
                    <Controller
                      name="hasProcessEval"
                      control={control}
                      render={({field}) => (
                        <FormControlLabel
                          sx={{".MuiFormControlLabel-label": {fontSize: 14, color: "#081f3c"}}}
                          control={
                            <Checkbox
                              id="hasProcessEval"
                              onChange={(e) => {
                                field.onChange(e.target.checked);
                                setValue("base64Process", "");
                                setValue("processFile", "");
                              }}
                              checked={field.value}
                              sx={{"& .MuiSvgIcon-root": {fontSize: 20}}}
                            />
                          }
                          label="해당없음"
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell component="td">
                    {watch("hasProcessEval") ? (
                      "해당없음"
                    ) : (
                      <MWInputFile file="base64Process" fileName="processFile" />
                    )}
                  </TableCell>
                  <TableCell component="th">
                    안전운전절차 <br />
                    <Controller
                      name="hasSafetyProcedure"
                      control={control}
                      render={({field}) => (
                        <FormControlLabel
                          sx={{".MuiFormControlLabel-label": {fontSize: 14, color: "#081f3c"}}}
                          control={
                            <Checkbox
                              id="hasSafetyProcedure"
                              onChange={(e) => {
                                field.onChange(e.target.checked);
                                setValue("base64Procedure", "");
                                setValue("procedureFile", "");
                              }}
                              checked={field.value}
                              sx={{"& .MuiSvgIcon-root": {fontSize: 20}}}
                            />
                          }
                          label="해당없음"
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell component="td">
                    {watch("hasSafetyProcedure") ? (
                      "해당없음"
                    ) : (
                      <MWInputFile file="base64Procedure" fileName="procedureFile" />
                    )}
                  </TableCell>
                  <TableCell component="th">
                    비상조치계획 <br />
                    <Controller
                      name="hasEmergency"
                      control={control}
                      render={({field}) => (
                        <FormControlLabel
                          sx={{".MuiFormControlLabel-label": {fontSize: 14, color: "#081f3c"}}}
                          control={
                            <Checkbox
                              id="hasEmergency"
                              onChange={(e) => {
                                field.onChange(e.target.checked);
                                setValue("base64Emergency", "");
                                setValue("emergencyFile", "");
                              }}
                              checked={field.value}
                              sx={{"& .MuiSvgIcon-root": {fontSize: 20}}}
                            />
                          }
                          label="해당없음"
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell component="td">
                    {watch("hasEmergency") ? (
                      "해당없음"
                    ) : (
                      <MWInputFile file="base64Emergency" fileName="emergencyFile" />
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th">
                    근로자교육평가 <br />
                    <Controller
                      name="hasEducation"
                      control={control}
                      render={({field}) => (
                        <FormControlLabel
                          sx={{".MuiFormControlLabel-label": {fontSize: 14, color: "#081f3c"}}}
                          control={
                            <Checkbox
                              id="hasEducation"
                              onChange={(e) => {
                                field.onChange(e.target.checked);
                                setValue("base64Education", "");
                                setValue("educationFile", "");
                              }}
                              checked={field.value}
                              sx={{"& .MuiSvgIcon-root": {fontSize: 20}}}
                            />
                          }
                          label="해당없음"
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell component="td">
                    {watch("hasEducation") ? (
                      "해당없음"
                    ) : (
                      <MWInputFile file="base64Education" fileName="educationFile" />
                    )}
                  </TableCell>
                  <TableCell component="th">가동전점검</TableCell>
                  <TableCell component="td">
                    <MWInputFile file="base64PreCheck" fileName="preCheckFile" />
                  </TableCell>
                  <TableCell component="th"></TableCell>
                  <TableCell component="td"></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </StyledDetailTable>
        </StyledDetailContainer>
      )}
    </React.Fragment>
  );
};

export default ChangeRequestForm;
